/**
 * MemberContributeModal — shared modal for contributing a photo, video,
 * document or admin message tagged to a family member. POSTs to
 * /api/family-trees/{treeId}/contributions. Used from the member profile
 * page, the tree Member Popup and the Member Edit page.
 */
import { useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2 } from "lucide-react";

const TITLES = {
  photo: "Upload a photo",
  video: "Submit a video link",
  document: "Upload a document",
  message: "Message the family admin",
};

export default function MemberContributeModal({ kind, treeId, memberId, memberName, onClose, onDone }) {
  const [caption, setCaption] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const [dataUrl, setDataUrl] = useState("");
  const [fileName, setFileName] = useState("");
  const [setAsProfile, setSetAsProfile] = useState(false);
  const [busy, setBusy] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Please pick a file under 8 MB.");
      return;
    }
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => setDataUrl(reader.result);
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { kind, member_id: memberId, caption: caption.trim() || undefined };
      if (kind === "photo" || kind === "document") {
        if (!dataUrl) { setBusy(false); return toast.error("Please pick a file."); }
        payload.data_url = dataUrl;
        if (kind === "photo" && setAsProfile) payload.set_as_profile = true;
      } else if (kind === "video") {
        if (!videoUrl.trim()) { setBusy(false); return toast.error("Paste a video link."); }
        payload.video_url = videoUrl.trim();
      } else if (kind === "message") {
        if (messageBody.trim().length < 3) { setBusy(false); return toast.error("Message is too short."); }
        payload.message = messageBody.trim();
      }
      const { data } = await http.post(`/family-trees/${treeId}/contributions`, payload);
      if (payload.set_as_profile) {
        if (data?.profile_photo_set) toast.success("Profile photo updated!");
        else toast.info("Photo added — an admin will confirm the profile photo change.");
      }
      onDone?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[130] bg-[#14213D]/30 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        className="wofy-card w-full max-w-lg text-left max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid={`contrib-modal-${kind}`}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="overline">CONTRIBUTE · {kind.toUpperCase()}</div>
            <h3 className="font-display text-lg mt-0.5">{TITLES[kind]}</h3>
            {memberName && (
              <div className="text-xs opacity-70 mt-0.5">Tagged with <b>{memberName}</b></div>
            )}
          </div>
          <button type="button" onClick={onClose} className="p-1 opacity-60 hover:opacity-100" data-testid="contrib-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {(kind === "photo" || kind === "document") && (
          <div className="mb-3">
            <label className="text-xs font-medium mb-1 block">Choose file</label>
            <input
              type="file"
              accept={kind === "photo" ? "image/*" : ".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,image/*"}
              onChange={onFile}
              className="text-sm block w-full"
              data-testid={`contrib-file-${kind}`}
            />
            {fileName && <div className="text-xs opacity-60 mt-1">Selected: {fileName}</div>}
            {kind === "photo" && memberId && (
              <label className="flex items-center gap-2 mt-2 text-xs cursor-pointer select-none" data-testid="contrib-set-profile-label">
                <input
                  type="checkbox"
                  checked={setAsProfile}
                  onChange={(e) => setSetAsProfile(e.target.checked)}
                  data-testid="contrib-set-profile-checkbox"
                />
                Set as Profile Photo{memberName ? ` for ${memberName}` : ""}
              </label>
            )}
          </div>
        )}

        {kind === "video" && (
          <div className="mb-3">
            <label className="text-xs font-medium mb-1 block">Video URL</label>
            <input
              type="url"
              className="wofy-input"
              placeholder="YouTube, Vimeo, or any public video link"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              data-testid="contrib-video-url"
            />
          </div>
        )}

        {kind === "message" && (
          <div className="mb-3">
            <label className="text-xs font-medium mb-1 block">Message</label>
            <textarea
              dir="auto"
              className="wofy-input"
              rows={5}
              maxLength={4000}
              placeholder="Anything you'd like the family admin to know about this member…"
              value={messageBody}
              onChange={(e) => setMessageBody(e.target.value)}
              data-testid="contrib-message-body"
            />
          </div>
        )}

        {kind !== "message" && (
          <div className="mb-3">
            <label className="text-xs font-medium mb-1 block">Caption (optional)</label>
            <input
              dir="auto"
              className="wofy-input"
              maxLength={800}
              placeholder="A short note describing this contribution"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              data-testid="contrib-caption"
            />
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="wofy-btn-ghost text-sm">Cancel</button>
          <button type="submit" disabled={busy} className="wofy-btn-primary text-sm" data-testid="contrib-submit">
            {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>) : "Submit"}
          </button>
        </div>
      </form>
    </div>
  );
}
